import { MapContainer, TileLayer, Polyline, Marker, Popup, Pane, useMap } from 'react-leaflet';
import { divIcon } from 'leaflet';
import { useEffect } from 'react';
import { fmtDateTime, fmtTime } from '../utils/date';
import type { ActiveGuard, HistoryTrack, MapPin, Drone } from '../types';

interface Props {
  guards: ActiveGuard[];
  alarmPins?: MapPin[];
  incidentPins?: MapPin[];
  /** Completed patrols from the last 24h — drawn faded, beneath everything. */
  historyTracks?: HistoryTrack[];
  drones?: Drone[];
  centre?: [number, number];
  zoom?: number;
  className?: string;
}

// Anything not belonging to a patrol happening RIGHT NOW is drawn back at
// these opacities. The point of the live map is what is happening now; the
// last 24h is context, and at full strength it competes with the thing the
// control room is actually meant to be looking at.
const PAST_TRACK_OPACITY = 0.25;
const PAST_PIN_OPACITY = 0.45;

// Past trails go in their own Leaflet pane BELOW the default overlay pane
// (which is 400). Ordering the JSX is not enough: react-leaflet appends a
// path to the pane when the component MOUNTS, and the live guard trail
// mounts as soon as a location arrives — before the history fetch resolves —
// so it was appended first and every history trail painted over the top of
// it. A pane's z-index is CSS and does not care about mount order.
const HISTORY_PANE = 'patrolit-history';
const HISTORY_PANE_Z = 390;

// Rose for completed trails. Grey at 25% turned out to be genuinely hard to
// find on OSM's own greys and beiges — a just-ended patrol looked like a map
// artefact. Rose is the one strong hue the basemap does not already use:
// blue is water, green is parkland, grey is roads and buildings. Note the
// answer was a distinct HUE at a readable opacity, not a fainter colour —
// fading it further would have made it worse, not softer.
const PAST_TRACK_COLOUR = '#e11d48';

// Stacking bands. Leaflet derives a marker's z-index from its pixel y, so
// pins at the same spot tie and DOM order decides; zIndexOffset overrides
// that. Bands are 1000 apart and each pin adds its recency rank within the
// band, so newest always sits on top of older of the same kind, and a live
// alarm always beats everything older or less urgent.
const Z_LIVE_ALARM = 4000;
const Z_LIVE_INCIDENT = 3000;
const Z_PAST_ALARM = 2000;
const Z_PAST_INCIDENT = 1000;

/** Rank pins oldest→newest and hand back a z-offset per pin id. Capped at 999
 *  so a busy band cannot climb into the one above it. */
function recencyRanks(pins: MapPin[], base: number): Map<string, number> {
  const ordered = [...pins].sort((a, b) => a.raised_at - b.raised_at);
  return new Map(ordered.map((p, i) => [p.id, base + Math.min(i, 999)]));
}

function MapCentrer({ centre }: { centre: [number, number] | undefined }) {
  const map = useMap();
  useEffect(() => {
    if (centre) map.flyTo(centre, map.getZoom(), { duration: 1 });
  }, [centre, map]);
  return null;
}

function guardIcon(colour: string, name: string) {
  return divIcon({
    className: '',
    html: `
      <div style="
        background:${colour};
        color:#fff;
        border-radius:50% 50% 50% 0;
        width:24px;height:24px;
        box-sizing:border-box;
        border:2px solid #fff;
        display:flex;align-items:center;justify-content:center;
        font-size:10px;font-weight:600;font-family:sans-serif;
        box-shadow:0 1px 4px rgba(0,0,0,.35);
        transform:rotate(-45deg);
      ">
        <span style="transform:rotate(45deg)">${name.charAt(0).toUpperCase()}</span>
      </div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -28],
  });
}

function alarmIcon(live: boolean) {
  return divIcon({
    className: '',
    html: `<div style="
      background:#dc2626;color:#fff;border-radius:50%;
      width:30px;height:30px;display:flex;align-items:center;
      justify-content:center;font-size:16px;
      box-shadow:0 2px 6px rgba(0,0,0,.5);
      opacity:${live ? 1 : PAST_PIN_OPACITY};
      border:2px solid #fff;">🚨</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -18],
  });
}

function incidentIcon(live: boolean) {
  return divIcon({
    className: '',
    html: `<div style="
      background:#f59e0b;color:#fff;border-radius:50%;
      width:28px;height:28px;display:flex;align-items:center;
      justify-content:center;font-size:14px;
      box-shadow:0 2px 6px rgba(0,0,0,.4);
      opacity:${live ? 1 : PAST_PIN_OPACITY};
      border:2px solid #fff;">⚠️</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -16],
  });
}

function droneIcon(flying: boolean) {
  return divIcon({
    className: '',
    html: `<div style="
      background:${flying ? '#2563eb' : '#6b7280'};color:#fff;border-radius:50%;
      width:30px;height:30px;display:flex;align-items:center;
      justify-content:center;font-size:16px;
      box-shadow:0 2px 6px rgba(0,0,0,.5);
      border:2px solid #fff;">🚁</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -18],
  });
}

export default function LiveMap({
  guards,
  alarmPins = [],
  incidentPins = [],
  historyTracks = [],
  drones = [],
  centre,
  zoom = 15,
  className = 'h-full w-full',
}: Props) {
  const defaultCentre: [number, number] = centre ?? [-33.9249, 18.4241];

  // A pin is "live" if its patrol is one of the patrols happening now. Keyed
  // on patrol_id rather than guard_id: the same guard walking a second lap is
  // a different patrol, and last lap's incident is history by then.
  const livePatrolIds = new Set(guards.map((g) => g.patrol_id));
  const isLive = (pin: MapPin) => livePatrolIds.has(pin.patrol_id);

  // Rank within each band separately, so a live pin is never outranked by a
  // past one however recent the past one is.
  const alarmZ = new Map([
    ...recencyRanks(alarmPins.filter((p) => !isLive(p)), Z_PAST_ALARM),
    ...recencyRanks(alarmPins.filter(isLive), Z_LIVE_ALARM),
  ]);
  const incidentZ = new Map([
    ...recencyRanks(incidentPins.filter((p) => !isLive(p)), Z_PAST_INCIDENT),
    ...recencyRanks(incidentPins.filter(isLive), Z_LIVE_INCIDENT),
  ]);

  // Polylines have no z-index — SVG paint order is the stacking order — so
  // recency has to be draw order. Backend already sends oldest first; sorting
  // here too keeps that true if the endpoint's ordering ever changes.
  // Drawn as one multi-segment path, so ordering no longer affects appearance —
  // a single stroke has no siblings to paint over. Kept sorted anyway so the
  // segment order stays stable between renders.
  const historySegments = [...historyTracks]
    .sort((a, b) => (a.started_at ?? 0) - (b.started_at ?? 0))
    .filter((t) => t.points.length >= 2)
    .map((t) => t.points);
  const orderedGuards = [...guards].sort((a, b) => a.last_seen - b.last_seen);

  return (
    <MapContainer center={defaultCentre} zoom={zoom} className={className} style={{ zIndex: 0 }}>
      {/* Street names on the demo are a real, unsolved want — but not this way.
          Tile labels are rasterised into the image, so the only lever is the
          basemap, and every keyless label-free option was tried and rejected on
          2026-08-31:

            - CARTO light_nolabels returns HTTP 200 and a valid PNG, then
              watermarks every tile "API KEY REQUIRED". A status-code check does
              not catch this; the image has to be looked at.
            - Esri World_Light_Gray_Base serves fine without a key but bakes its
              own labels in ("STRAND ST", "N2" are legible at z15).
            - Stadia/Stamen, MapTiler and Mapbox all require keys for their
              label-free styles.

          So it needs either a (free) API key, or the demo relocated to somewhere
          unrelated — which fixes the underlying concern properly, since the
          coordinates are in the API responses regardless of the basemap. */}
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapCentrer centre={centre} />
      {/* Faded backdrop first, so live trails and pins draw over it. One
          colour rather than each guard's own: a wall of differing colours
          reads as "something is happening here", which is wrong for patrols
          that already finished. Oldest first — later siblings paint on top. */}
      <Pane name={HISTORY_PANE} style={{ zIndex: HISTORY_PANE_Z }}>
        {/* ONE Polyline holding every past track as a separate segment, not one
            Polyline each. Semi-transparent strokes COMPOUND where they overlap:
            with up to 60 patrols round the same estate roads, ground covered
            repeatedly was painted repeatedly and went almost black, so the more
            thoroughly an area had been patrolled the less readable it became —
            exactly backwards. Leaflet accepts an array of position arrays and
            renders it as a single SVG path, which is stroked once, so a road
            covered sixty times looks identical to one covered once.

            Geometry is untouched: each track stays its own segment, so no line
            is joined to its neighbour and nothing is deduplicated away. */}
        {historySegments.length > 0 && (
          <Polyline
            positions={historySegments}
            pathOptions={{ color: PAST_TRACK_COLOUR, weight: 3,
                           opacity: PAST_TRACK_OPACITY,
                           lineCap: 'round', lineJoin: 'round' }}
          />
        )}
      </Pane>
      {orderedGuards.map((g) => (
        <GuardTrail key={g.guard_id} guard={g} />
      ))}
      {/* zIndexOffset: Leaflet derives a marker's z-index from its pixel y, so
          two pins at the SAME point tie and DOM order decides — incidents are
          rendered after alarms below, so an alarm at a repeat hotspot (a gate,
          the guardhouse) was painted UNDER the incident and effectively
          invisible. Observed on the demo: five alarms buried under three
          incidents on one waypoint. An alarm must never lose that race. */}
      {alarmPins.map((pin) => (
        <Marker key={`alarm-${pin.id}`} position={[pin.lat, pin.lng]}
                icon={alarmIcon(isLive(pin))}
                zIndexOffset={alarmZ.get(pin.id) ?? Z_PAST_ALARM}>
          <Popup>
            <strong>🚨 Alarm</strong><br />
            <span>{pin.guard_name}</span><br />
            <span className="text-xs text-gray-500">
              {fmtDateTime(pin.raised_at)}
            </span>
            {/* Two labelled lines, not one blob: what the patroller saw is a
                different fact from what the control room did about it, and
                the first is the one that ends up in an insurance claim. */}
            {pin.notes && (
              <><br /><span className="text-xs text-gray-700 whitespace-pre-wrap">
                <span className="text-gray-400">Patroller: </span>{pin.notes}
              </span></>
            )}
            {pin.ack_notes && (
              <><br /><span className="text-xs text-gray-700 whitespace-pre-wrap">
                <span className="text-gray-400">Control room: </span>{pin.ack_notes}
              </span></>
            )}
          </Popup>
        </Marker>
      ))}
      {incidentPins.map((pin) => (
        <Marker key={`incident-${pin.id}`} position={[pin.lat, pin.lng]}
                icon={incidentIcon(isLive(pin))}
                zIndexOffset={incidentZ.get(pin.id) ?? Z_PAST_INCIDENT}>
          <Popup>
            <strong>⚠️ Incident</strong><br />
            <span>{pin.guard_name}</span><br />
            <span className="text-xs text-gray-500">
              {fmtDateTime(pin.raised_at)}
            </span>
            {pin.notes && (
              <><br /><span className="text-xs text-gray-700 whitespace-pre-wrap">{pin.notes}</span></>
            )}
          </Popup>
        </Marker>
      ))}
      {drones.filter((d) => d.flying && d.track.length >= 2).map((d) => (
        <Polyline
          key={`drone-track-${d.drone_id}`}
          positions={d.track}
          pathOptions={{ color: '#2563eb', weight: 3, opacity: 0.8, dashArray: '6 6' }}
        />
      ))}
      {drones.filter((d) => d.lat !== null && d.lng !== null).map((d) => (
        <Marker key={d.drone_id} position={[d.lat as number, d.lng as number]} icon={droneIcon(d.flying)}>
          <Popup>
            <strong>🚁 {d.drone_id}</strong><br />
            {d.mission_state.replace(/_/g, ' ')}<br />
            {(d.lat as number).toFixed(5)}, {(d.lng as number).toFixed(5)}<br />
            {d.altitude_agl_m.toFixed(0)}m AGL / {d.altitude_asl_m.toFixed(0)}m ASL<br />
            {d.speed_kmh.toFixed(0)} km/h · {d.battery_pct.toFixed(0)}% battery<br />
            <span className="text-xs text-gray-500">
              Last seen: {fmtTime(d.last_seen)}
            </span>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}

function GuardTrail({ guard }: { guard: ActiveGuard }) {
  const hasPosition = guard.lat !== 0 || guard.lng !== 0;

  return (
    <>
      {guard.track.length >= 2 && (
        <Polyline
          positions={guard.track}
          pathOptions={{ color: guard.colour, weight: 4, opacity: 1,
                         lineCap: 'round', lineJoin: 'round' }}
        />
      )}
      {hasPosition && (
        <Marker position={[guard.lat, guard.lng]} icon={guardIcon(guard.colour, guard.guard_name)}>
          <Popup>
            <strong>{guard.guard_name}</strong><br />
            {guard.lat.toFixed(5)}, {guard.lng.toFixed(5)}<br />
            <span className="text-xs text-gray-500">
              Last seen: {fmtTime(guard.last_seen)}
            </span>
          </Popup>
        </Marker>
      )}
    </>
  );
}
